import { useCallback, useEffect, useState } from "react";
import BoardFundraisingPhotoEditor from "./BoardFundraisingPhotoEditor";

const blank = { title: "", subtitle: "", description: "", starts_at: "", timezone: "Europe/London" };

export default function BoardFundraisingCampaignsPanel({ client }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [reuse, setReuse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [emailEvent, setEmailEvent] = useState(null);
  const [emailKey, setEmailKey] = useState("registration_confirmation");
  const [template, setTemplate] = useState(null);
  const load = useCallback(async () => { const response = await client.get("/admin/board-fundraising/events"); setEvents(response.data.events || []); }, [client]);
  useEffect(() => { load().catch(() => setError("Campaigns could not be loaded.")); }, [load]);
  const action = async (operation) => {
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await operation();
      if (response?.data?.event?.setup_error) setError(response.data.event.setup_error);
      else setMessage("Campaign saved. Only Approve / Publish opens public registration.");
      await load();
      return true;
    } catch (err) { setError(err.response?.data?.detail || "The campaign could not be updated."); return false; }
    finally { setBusy(false); }
  };
  const select = (event, copy) => {
    setEditing(copy ? null : event.event_id); setReuse(copy ? event.event_id : null);
    setForm({ title: event.title, subtitle: event.subtitle || "", description: event.description || "",
      starts_at: copy ? "" : event.starts_at, timezone: event.timezone || "Europe/London" });
  };
  const editEmail = async (event, key = "registration_confirmation") => {
    setEmailEvent(event); setEmailKey(key);
    try {
      const response = await client.get("/admin/board-fundraising/email-templates");
      setTemplate(event.email_templates?.[key] || response.data.templates.find(item => item.template_key === key));
    } catch { setError("Email templates could not be loaded."); }
  };
  const save = async (e) => {
    e.preventDefault();
    const root = "/admin/board-fundraising/events";
    const ok = await action(() => editing ? client.patch(root + "/" + editing, form)
      : reuse ? client.post(root + "/" + reuse + "/clone", form) : client.post(root, form));
    if (ok) { setForm(blank); setEditing(null); setReuse(null); }
  };
  return <div className="bf-admin-data-view">
    <h2>Live Event Campaigns</h2>
    <BoardFundraisingPhotoEditor client={client}/>
    <p>Start a campaign to prepare its Zoom meeting and registration draft. Review the copy before publishing.</p>
    {error && <p role="alert" className="bf-admin-data-error">{error}</p>}
    {message && <p role="status">{message}</p>}
    {emailEvent && template && <form className="bf-admin-event-form" onSubmit={async e => {
      e.preventDefault(); await action(() => client.put(`/admin/board-fundraising/events/${emailEvent.event_id}/email-templates/${emailKey}`, template));
      setEmailEvent(null);
    }}><h3>Email copy for session {emailEvent.session_number}</h3>
      <label>Message type<select value={emailKey} onChange={e => editEmail(emailEvent, e.target.value)}>
        {["registration_confirmation", "reminder_24h", "reminder_5m", "recording_followup"].map(key => <option key={key}>{key}</option>)}
      </select></label>
      <label>Subject<input value={template.subject} onChange={e => setTemplate({ ...template, subject: e.target.value })}/></label>
      <label>Message<textarea rows={8} value={template.body} onChange={e => setTemplate({ ...template, body: e.target.value })}/></label>
      <label><input type="checkbox" checked={!!template.enabled} onChange={e => setTemplate({ ...template, enabled: e.target.checked })}/> Enable this campaign message</label>
      <button className="button" disabled={busy}>Save campaign email</button>
      <button className="button button-back" type="button" onClick={() => setEmailEvent(null)}>Close</button>
    </form>}
    <form className="bf-admin-event-form" onSubmit={save}>
      <h3>{editing ? "Edit landing-page draft" : reuse ? "Reuse campaign with a new date" : "Start New Campaign"}</h3>
      {[["title", "Topic / title"], ["subtitle", "Subtitle"], ["description", "Event details / landing-page copy"],
        ["starts_at", "Date and time (local time or ISO date with UTC offset)"], ["timezone", "Timezone"]].map(([key, label]) =>
        <label key={key}>{label}{key === "description"
          ? <textarea rows={5} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}/>
          : <input required={["title", "starts_at", "timezone"].includes(key)} value={form[key]}
              placeholder={key === "starts_at" ? "2027-01-15T19:00" : undefined}
              onChange={e => setForm({ ...form, [key]: e.target.value })}/>}</label>)}
      <button className="button" disabled={busy}>{editing ? "Save draft for review" : "Start Campaign"}</button>
      {(editing || reuse) && <button type="button" className="button button-back" onClick={() => { setEditing(null); setReuse(null); setForm(blank); }}>Cancel editing</button>}
    </form>
    <div className="bf-admin-event-list"><h3>Sessions ({events.length}/62)</h3>
      {events.map(event => <article className="bf-admin-event-card" key={event.event_id}>
        <h3>Session {event.session_number}: {event.title}</h3>
        <p>{event.subtitle}</p><p>{event.description}</p>
        <p>{event.starts_at} · {event.timezone} · {event.status}</p>
        {event.setup_error && <p className="bf-admin-data-error">{event.setup_error}</p>}
        <button className="button button-small" disabled={busy || ["completed", "cancelled"].includes(event.status)} onClick={() => select(event, false)}>Edit landing page</button>{" "}
        <button className="button button-small" disabled={busy} onClick={() => select(event, true)}>Reuse campaign</button>{" "}
        <button className="button button-small" disabled={busy} onClick={() => editEmail(event)}>Edit campaign emails</button>{" "}
        {event.status === "draft" && <>
          {!event.zoom_meeting_id && <button className="button button-small" disabled={busy} onClick={() => action(() => client.post(`/admin/board-fundraising/events/${event.event_id}/prepare`))}>Retry Zoom preparation</button>}{" "}
          <button className="button button-small" disabled={busy || !event.zoom_meeting_id || !event.zoom_join_url}
            onClick={() => action(() => client.patch(`/admin/board-fundraising/events/${event.event_id}`, { status: "scheduled" }))}>Approve / Publish</button>
        </>}
        {event.status === "scheduled" && <p><a href={`/board-fundraising/events/${event.event_id}/register`} target="_blank" rel="noreferrer">Open public registration page</a></p>}
        {event.status === "scheduled" && <>
          <button className="button button-back button-small" disabled={busy} onClick={() => action(() => client.patch(`/admin/board-fundraising/events/${event.event_id}`, { status: "draft" }))}>Unpublish registration</button>{" "}
          <button className="button button-small" disabled={busy} onClick={() => action(() => client.patch(`/admin/board-fundraising/events/${event.event_id}`, { status: "completed" }))}>Mark event complete</button>{" "}
        </>}
        {event.zoom_join_url && <p>Attendee link: <a href={event.zoom_join_url}>{event.zoom_join_url}</a></p>}
        {["draft", "scheduled"].includes(event.status) && <button className="button button-back button-small" disabled={busy} onClick={() => action(() => client.patch(`/admin/board-fundraising/events/${event.event_id}`, { status: "cancelled" }))}>Cancel campaign</button>}
      </article>)}
    </div>
  </div>;
}
